'use strict'

import Polyfills from './Polyfills.js'
import { Bartender } from '@fokke-/bartender.js'
import boringmenu from '@teppokoivula/boringmenu'
import Glightbox from 'glightbox'

/**
 * Site class contains general purpose site-specific features.
 *
 * @version 1.1.0
 */
export default class Site {
	/**
	 * Class constructor
	 *
	 * @param {Object} options Options for the class.
	 */
	constructor(options = {}) {
		this.options = {}

		// Init polyfills
		const polyfills = new Polyfills()
		polyfills.init()

		// Init off-canvas after mobile menu is ready
		document.addEventListener('boringmenu-init-done', () => {
			window.bartender = new Bartender({
				el: document.querySelector('.bartender-main'),
				contentEl: document.querySelector('.bartender-content'),
			}, {
				focusTrap: true,
			})

			window.bartender.addBar('mobile-bar', {
				el: document.querySelector('.mobile-bar'),
				position: 'right',
				mode: 'float',
			})

			document.querySelector('.js-mobile-menu-toggle').addEventListener('click', (event) => {
				window.bartender.toggle('mobile-bar', event.target)
			})

			document.querySelector('.js-close-mobile-bar').addEventListener('click', (event) => {
				window.bartender.close()
			})
		})

		// Init mobile menu
		const mobileMenu = document.getElementById('mobile-menu')
		new boringmenu({
			selectors: {
				menu: '.menu-mobile__list--level-1',
			},
			classes: {
				item: 'menu-mobile__item',
				itemActive: 'menu-mobile__item--current',
				itemParent: 'menu-mobile__item--parent',
				toggle: 'menu-mobile__toggle',
				toggleTextContainer: 'sr-only',
			},
			labels: {
				'menu.open': mobileMenu
					? mobileMenu.getAttribute('data-labels-open')
					: 'open',
				'menu.close': mobileMenu
					? mobileMenu.getAttribute('data-labels-close')
					: 'close',
			},
			icons: {
				'menu.open': 'icon-open',
				'menu.close': 'icon-close',
			},
		})

		// Initialize
		this.init(options)
	}

	/**
	 * Init the class by calling applicable init methods
	 *
	 * @param {Object} options Options for the class.
	 * @return {Object}
	 */
	init(options = {}) {
		// Merge user options to the defaults
		this.options = {
			responsiveTables: {
				selector: 'main table',
			},
			imageLinks: {
				parentSelector: 'main',
			},
			...options,
		}

		// Call individual init methods
		this.initResponsiveTables()
		this.initSkipLinks()
		this.initImageLinks()
		this.initCardFilters()

		// Dispatch custom event when init is done
		document.dispatchEvent(
			new CustomEvent('site-init-done', {
				bubbles: true,
				cancelable: true,
			})
		)

		return this
	}

	/**
	 * Initialize responsive tables
	 *
	 * Finds content tables and wraps them with div.table-wrapper.
	 */
	initResponsiveTables() {
		document
			.querySelectorAll(this.options.responsiveTables.selector)
			.forEach((table) => {
				if (!table.closest('.table-wrapper')) {
					const tableWrapper = document.createElement('div')
					tableWrapper.classList.add('table-wrapper')
					tableWrapper.classList.add('overflow-x-auto')
					table.parentNode.insertBefore(tableWrapper, table)
					tableWrapper.appendChild(table)
				}
			})
	}

	/**
	 * Initialize skip links
	 *
	 * Finds skip links and enhances their behaviour for various screen readers and mobile devices.
	 */
	initSkipLinks() {
		const skipLinks = document.querySelectorAll(
			'.skip-link:not([data-skip-link])'
		)
		if (skipLinks.length) {
			const skipToBlur = (event) => {
				if (event.target.getAttribute('data-tabindex')) {
					event.target.removeAttribute('tabindex')
					event.target.removeAttribute('data-tabindex')
				}
			}
			skipLinks.forEach((skipLink) => {
				skipLink.setAttribute('data-skip-link', true)
				skipLink.addEventListener('click', (event) => {
					const skipTo = document.getElementById(
						event.target.href.split('#')[1]
					)
					if (skipTo && skipTo.getAttribute('tabindex') != '-1') {
						event.preventDefault()
						skipTo.setAttribute('tabindex', '-1')
						skipTo.setAttribute('data-tabindex', true)
						skipTo.addEventListener('blur', skipToBlur)
						skipTo.addEventListener('focusout', skipToBlur)
						skipTo.focus()
					}
				})
			})
		}
	}

	/**
	 * Initialize image links
	 */
	initImageLinks() {
		// Parent of image links
		let parentNode = document.querySelector(
			this.options.imageLinks.parentSelector
		)
		if (!parentNode) return

		// Add glightbox class to image links
		parentNode
			.querySelectorAll(
				'a[href$=".jpg"], a[href$=".jpeg"], a[href$=".png"], a[href$=".gif"]'
			)
			.forEach((link) => {
				if (!link.classList.contains('glightbox')) {
					if (
						!link.getAttribute('data-title') &&
						!link.getAttribute('data-glightbox')
					) {
						let figcaption = link.parentNode.querySelector('figcaption')
						if (figcaption) {
							let caption = figcaption ? figcaption.textContent : ''
							link.setAttribute('data-title', caption)
						}
					}
					link.classList.add('glightbox')
				}
			})

		// Initialize GLightbox
		if (parentNode.querySelector('.glightbox')) {
			window.glightbox = Glightbox()
		}
	}

	/**
	 * Initialize card filters
	 *
	 * @param {string} selector Selector for the card filter.
	 */
	initCardFilters() {
		const cardFilters = document.querySelectorAll('.card-filters')
		if (cardFilters.length) {
			const cardFiltersCache = {}
			cardFilters.forEach((form) => {
				form.addEventListener('submit', (event) => {
					event.preventDefault()
					if (form.hasAttribute('data-submitting')) {
						return
					}
					form.setAttribute('data-submitting', true)
					const resultsContainer = form.parentElement.querySelector('.card-filters__results-container')
					resultsContainer.setAttribute('aria-busy', true)
					const formData = new FormData(form)
					const urlParams = new URLSearchParams(window.location.search)
					for (const [key, value] of formData.entries()) {
						if (value) {
							urlParams.set(key, value)
						} else {
							urlParams.delete(key)
						}
					}
					window.history.replaceState(
						null,
						'',
						`${window.location.pathname}?${urlParams}`
					)
					if (Object.prototype.hasOwnProperty.call(cardFiltersCache, window.location.href)) {
						resultsContainer.innerHTML = ''
						resultsContainer.insertAdjacentHTML(
							'beforeend',
							cardFiltersCache[window.location.href]
						)
						form.removeAttribute('data-submitting')
						resultsContainer.removeAttribute('aria-busy')
						return
					}
					fetch(window.location.href + (window.location.href.endsWith('?') ? '' : '&') + 'ajax=1', {
						method: 'GET',
						headers: {
							'X-Requested-With': 'XMLHttpRequest',
						},
					})
						.then((response) => {
							if (response.ok) {
								return response.text()
							}
							throw new Error('Network response was not ok')
						})
						.then((html) => {
							resultsContainer.innerHTML = ''
							resultsContainer.insertAdjacentHTML(
								'beforeend',
								html
							)
						})
						.catch((error) => {
							console.error('Fetch error:', error)
						})
						.finally(() => {
							cardFiltersCache[window.location.href] = resultsContainer.innerHTML
							form.removeAttribute('data-submitting')
							resultsContainer.removeAttribute('aria-busy')
						})
				})
				form.querySelectorAll('select').forEach((select) => {
					select.addEventListener('change', () => {
						form.dispatchEvent(new Event('submit'), {
							bubbles: true,
							cancelable: true,
						})
					})
				})
			})
		}
	}
}
